import {
  aspectOrbDegrees,
  findAngle,
  findPlanet,
  getAspects,
  getCountedPlanets,
  isMajorAspectName,
  isPersonalPlanet,
  signElement,
} from '@/utils/chartDataAccessors';

// Harmonics are intentionally deferred until the astro/chart payload exposes reliable harmonic data.

export const FOCAL_OPTION_IDS = {
  EVOLUTIONARY_DIRECTION: 'evolutionary_direction',
  ELEMENT_PROFILE: 'element_profile',
  ANGULAR_PATTERN: 'angular_pattern',
  STELLIUM: 'stellium_concentration',
  TIGHT_ASPECT: 'tight_aspect',
};

export const LEGACY_FOCAL_OPTION_IDS = new Set([
  'overall_birth_chart_preview',
]);

const STELLIUM_MIN_COUNT = 3;
const TIGHT_ASPECT_MAX_ORB = 4;

function buildEvolutionaryDirectionOption(chartData) {
  const factors = ['Pluto', 'North Node', 'South Node', 'Moon']
    .map((name) => findPlanet(chartData, name))
    .filter(Boolean);

  return {
    id: FOCAL_OPTION_IDS.EVOLUTIONARY_DIRECTION,
    type: 'always_available',
    label: 'Your deeper life direction',
    short_note: 'A preview of the deeper pattern of growth, meaning, and direction suggested by your chart.',
    confidence: 'high',
    requires_birth_time: false,
    chart_factors: factors.map((planet) => planet.name.toLowerCase().replace(/\s+/g, '_')),
    report_prompt_key: 'evolutionary_direction',
    score: 100,
    metadata: {
      pluto_sign: findPlanet(chartData, 'Pluto')?.sign ?? null,
      north_node_sign: findPlanet(chartData, 'North Node')?.sign ?? null,
      south_node_sign: findPlanet(chartData, 'South Node')?.sign ?? null,
      moon_sign: findPlanet(chartData, 'Moon')?.sign ?? null,
    },
  };
}

function buildElementProfileOption(chartData) {
  const elementCounts = { fire: 0, earth: 0, air: 0, water: 0 };
  const planets = getCountedPlanets(chartData);

  for (const planet of planets) {
    const element = signElement(planet.sign);
    if (element) {
      elementCounts[element] += 1;
    }
  }

  const ranked = Object.entries(elementCounts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  const dominantElements = ranked.slice(0, 2).map(([element]) => element);

  return {
    id: FOCAL_OPTION_IDS.ELEMENT_PROFILE,
    type: 'always_available',
    label: 'Your element profile',
    short_note: 'A look at your natural balance of fire, earth, air, and water — how energy, feeling, thought, and embodiment tend to move through you.',
    confidence: planets.length ? 'high' : 'medium',
    requires_birth_time: false,
    chart_factors: planets.map((planet) => planet.name.toLowerCase()),
    report_prompt_key: 'element_profile',
    score: 95,
    metadata: {
      dominant_elements: dominantElements,
      element_counts: elementCounts,
    },
  };
}

function buildAngularPatternOption(chartData, birthTimeAccuracy) {
  if (birthTimeAccuracy === 'unknown') {
    return null;
  }

  const asc = findAngle(chartData, 'ASC');
  if (!asc?.sign) {
    return null;
  }

  const mc = findAngle(chartData, 'MC');
  const angleFactors = [
    ['asc', 'ASC'],
    ['mc', 'MC'],
    ['dsc', 'DSC'],
    ['ic', 'IC'],
  ]
    .filter(([, name]) => findAngle(chartData, name))
    .map(([factor]) => factor);

  const shortNote = birthTimeAccuracy === 'approximate'
    ? 'A time-sensitive view of your chart. Because your birth time is approximate, this should be read as suggestive rather than exact.'
    : 'A time-sensitive view of how your chart meets the world and how your life energy takes shape.';

  return {
    id: FOCAL_OPTION_IDS.ANGULAR_PATTERN,
    type: 'gated',
    label: 'Your rising and angles',
    short_note: shortNote,
    confidence: birthTimeAccuracy === 'approximate' ? 'medium' : 'high',
    requires_birth_time: true,
    chart_factors: angleFactors,
    report_prompt_key: 'angular_pattern',
    score: birthTimeAccuracy === 'approximate' ? 40 : 50,
    metadata: {
      asc_sign: asc.sign,
      mc_sign: mc?.sign ?? null,
    },
  };
}

function scoreStellium(planetsInSign) {
  const personalCount = planetsInSign.filter((planet) => isPersonalPlanet(planet.name)).length;
  const outerCount = planetsInSign.length - personalCount;
  return (planetsInSign.length * 12) + (personalCount * 8) + Math.min(outerCount, 2) * 2;
}

function detectStelliumOption(chartData) {
  const bySign = new Map();

  for (const planet of getCountedPlanets(chartData)) {
    if (!planet.sign) {
      continue;
    }
    const group = bySign.get(planet.sign) ?? [];
    group.push(planet);
    bySign.set(planet.sign, group);
  }

  let best = null;
  for (const [sign, planets] of bySign.entries()) {
    if (planets.length < STELLIUM_MIN_COUNT) {
      continue;
    }
    const score = scoreStellium(planets);
    if (!best || score > best.score) {
      best = { sign, planets, score };
    }
  }

  if (!best) {
    return null;
  }

  return {
    id: FOCAL_OPTION_IDS.STELLIUM,
    type: 'detected',
    label: 'A concentrated pattern in your chart',
    short_note: 'Several chart factors gather in one area, suggesting a strong recurring emphasis in your life.',
    confidence: best.planets.some((planet) => isPersonalPlanet(planet.name)) ? 'high' : 'medium',
    requires_birth_time: false,
    chart_factors: best.planets.map((planet) => planet.name.toLowerCase()),
    report_prompt_key: 'stellium_concentration',
    score: best.score,
    metadata: {
      sign: best.sign,
      planets: best.planets.map((planet) => planet.name),
    },
  };
}

function scoreTightAspect(aspect) {
  const orb = aspectOrbDegrees(aspect);
  if (orb == null || orb > TIGHT_ASPECT_MAX_ORB) {
    return null;
  }

  let score = (TIGHT_ASPECT_MAX_ORB - orb) * 20;
  if (isMajorAspectName(aspect.aspect)) {
    score += 15;
  }
  if (isPersonalPlanet(aspect.point_a) || isPersonalPlanet(aspect.point_b)) {
    score += 10;
  }
  return { score, orb };
}

function detectTightAspectOption(chartData) {
  let best = null;

  for (const aspect of getAspects(chartData)) {
    const scored = scoreTightAspect(aspect);
    if (!scored) {
      continue;
    }
    if (!best || scored.score > best.score) {
      best = { aspect, ...scored };
    }
  }

  if (!best) {
    return null;
  }

  const aspectName = (best.aspect.aspect ?? 'aspect').toLowerCase();

  return {
    id: FOCAL_OPTION_IDS.TIGHT_ASPECT,
    type: 'detected',
    label: 'A strong inner dynamic',
    short_note: 'A close relationship between chart factors that may describe a powerful tension, gift, or recurring theme.',
    confidence: best.orb <= 2 ? 'high' : 'medium',
    requires_birth_time: false,
    chart_factors: [best.aspect.point_a, best.aspect.point_b]
      .filter(Boolean)
      .map((name) => name.toLowerCase().replace(/\s+/g, '_')),
    report_prompt_key: 'tight_aspect',
    score: best.score,
    metadata: {
      aspect: aspectName,
      bodyA: best.aspect.point_a ?? '',
      bodyB: best.aspect.point_b ?? '',
      orb: Math.round(best.orb * 10) / 10,
    },
  };
}

function orderFocalOptions(options) {
  const byId = Object.fromEntries(options.map((option) => [option.id, option]));
  const ordered = [];

  for (const id of [FOCAL_OPTION_IDS.EVOLUTIONARY_DIRECTION, FOCAL_OPTION_IDS.ELEMENT_PROFILE]) {
    if (byId[id]) {
      ordered.push(byId[id]);
    }
  }

  const detected = options
    .filter((option) => option.type === 'detected')
    .sort((a, b) => b.score - a.score);
  ordered.push(...detected);

  if (byId[FOCAL_OPTION_IDS.ANGULAR_PATTERN]) {
    ordered.push(byId[FOCAL_OPTION_IDS.ANGULAR_PATTERN]);
  }

  return ordered;
}

/**
 * Detect chart-derived focal options for the discovery funnel.
 */
export function detectDiscoveryFocalOptions(chartData, {
  birthTimeAccuracy = 'known',
  chartSignature = '',
} = {}) {
  try {
    const candidates = [
      buildEvolutionaryDirectionOption(chartData),
      buildElementProfileOption(chartData),
      detectStelliumOption(chartData),
      detectTightAspectOption(chartData),
      buildAngularPatternOption(chartData, birthTimeAccuracy),
    ].filter(Boolean);

    const ordered = orderFocalOptions(candidates);

    if (ordered.length >= 2) {
      return ordered.map((option) => ({
        ...option,
        chart_signature: chartSignature || undefined,
      }));
    }
  } catch (error) {
    console.error('[discovery-focal] focal option detection failed', error);
  }

  return [
    buildEvolutionaryDirectionOption(chartData ?? {}),
    buildElementProfileOption(chartData ?? {}),
  ];
}

export function normalizeSelectedFocalOptionId(selectedId) {
  if (!selectedId || LEGACY_FOCAL_OPTION_IDS.has(selectedId)) {
    return null;
  }
  return selectedId;
}

export function resolveSelectedFocalOption(focalOptions, selectedId) {
  const options = Array.isArray(focalOptions) ? focalOptions : [];
  const normalizedId = normalizeSelectedFocalOptionId(selectedId)
    ?? FOCAL_OPTION_IDS.EVOLUTIONARY_DIRECTION;

  const found = options.find((option) => option.id === normalizedId);
  if (found) {
    return found;
  }

  return options[0] ?? buildEvolutionaryDirectionOption({});
}

export function formatFocalOptionMetadata(option) {
  if (!option?.metadata) {
    return '';
  }

  if (option.id === FOCAL_OPTION_IDS.ELEMENT_PROFILE) {
    const dominant = option.metadata.dominant_elements ?? [];
    if (!dominant.length) {
      return '';
    }
    const labels = dominant.map((element) => element.charAt(0).toUpperCase() + element.slice(1));
    if (labels.length === 1) {
      return `${labels[0]} is strongest in your chart.`;
    }
    return `${labels.join(' and ')} are strongest in your chart.`;
  }

  if (option.id === FOCAL_OPTION_IDS.STELLIUM) {
    const { sign, planets } = option.metadata;
    if (sign && Array.isArray(planets) && planets.length) {
      return `${planets.join(', ')} in ${sign}`;
    }
  }

  if (option.id === FOCAL_OPTION_IDS.TIGHT_ASPECT) {
    const { aspect, bodyA, bodyB, orb } = option.metadata;
    if (bodyA && bodyB && aspect) {
      const orbText = orb != null ? ` (${orb}°)` : '';
      return `${bodyA} ${aspect} ${bodyB}${orbText}`;
    }
  }

  if (option.id === FOCAL_OPTION_IDS.ANGULAR_PATTERN) {
    const { asc_sign: ascSign, mc_sign: mcSign } = option.metadata;
    if (ascSign && mcSign) {
      return `${ascSign} rising · ${mcSign} midheaven`;
    }
    if (ascSign) {
      return `${ascSign} rising`;
    }
  }

  return '';
}

export function ensureFocalOptions(state) {
  if (!state?.chartData) {
    return [];
  }
  if (Array.isArray(state.focalOptions) && state.focalOptions.length >= 2) {
    return state.focalOptions;
  }
  return detectDiscoveryFocalOptions(state.chartData, {
    birthTimeAccuracy: state.birthTimeAccuracy ?? state.chartMeta?.birthTimeAccuracy,
    chartSignature: state.chartSignature,
  });
}
